import { 
  collection, 
  doc, 
  getDoc, 
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { CalculatorFormData, CalculatorResults } from '../features/calculator/types';

export interface StoredReportData {
  reportId: string;
  sessionId?: string; // Original session ID for tracking
  finalizedAt: any; // Firestore timestamp
  leadSource?: string;

  // Critical fields for admin panel and routing
  hasROIAnalysis: boolean;
  reportType: 'TAS_ONLY' | 'ROI_ANALYSIS';
  organizationName: string;
  
  // All the data that ReportGenerator needs
  formData: CalculatorFormData;
  results: CalculatorResults;
  componentData: any[];
  improvementData: any[];
  enhancedData: {
    scenarioMode: 'conservative' | 'moderate' | 'optimistic';
    annualSubscription: number;
    implementationInvestment: number;
    paybackPeriod: number;
    year1ROI: number;
    steadyStateROI: number;
    enhancedSavings: any;
    topPerformerTAS: number;
    industryAverageTAS: number;
  };
  
  // Metadata
  metadata?: {
    browserInfo?: string;
    reportUrl?: string;
    emailSent?: boolean;
    emailSentAt?: any;
  };
}

/**
 * Save finalized report data to Firestore
 * @param userId - The hashed user ID
 * @param sessionId - The session ID
 * @param reportData - All the calculated data to store
 * @returns The generated report ID
 */
export async function saveFinalizedReport(
  userId: string,
  sessionId: string,
  reportData: Omit<StoredReportData, 'reportId' | 'finalizedAt'>
): Promise<string> {
  try {
    // Generate a unique report ID
    const reportId = doc(collection(db, 'temp')).id;
    
    // Create the document reference for user's session
    const reportRef = doc(db, 'users', userId, 'calculator_sessions', sessionId, 'finalized_reports', reportId);
    
    // Clean formData by removing undefined values (Firestore doesn't accept undefined)
    const cleanFormData = Object.entries(reportData.formData || {}).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    
    // Clean the entire reportData by removing undefined values
    const cleanReportData = Object.entries(reportData).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    
    // Save the report data with industry-standard timestamps
    const dataToSave: StoredReportData & {
      createdAt: any;
      updatedAt: any;
    } = {
      ...cleanReportData,  // Use cleaned reportData instead of raw reportData
      formData: cleanFormData,  // Use cleaned formData
      reportId,
      sessionId,  // Save the original session ID for tracking
      // Industry standard timestamps
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Domain-specific timestamp
      finalizedAt: serverTimestamp(),
      metadata: {
        ...reportData.metadata,
        browserInfo: navigator.userAgent,
        reportUrl: `/report/${reportId}`
      }
    };
    
    // Use batch write for atomic operations - all succeed or all fail
    const batch = writeBatch(db);
    
    // Add report to user's session collection
    batch.set(reportRef, dataToSave);
    
    // Add report to public reports collection
    const publicReportRef = doc(db, 'reports', reportId);
    batch.set(publicReportRef, {
      ...dataToSave,
      userId,
      sessionId
    });
    
    // Update session document with report ID and complete data
    const sessionRef = doc(db, 'users', userId, 'calculator_sessions', sessionId);
    // Determine the final step based on report type
    const finalStep = reportData.hasROIAnalysis || reportData.reportType === 'ROI_ANALYSIS' ? 4 : 3;

    // Prepare session update with complete data from the report
    const sessionUpdate: any = {
      reportId: reportId,  // Standardized field name
      finalizedAt: serverTimestamp(),  // Standardized field name
      updatedAt: serverTimestamp(),  // Always update the updatedAt timestamp
      currentStep: finalStep,  // Ensure we record the final step
      // Add critical fields for proper classification and display
      hasROIAnalysis: reportData.hasROIAnalysis,
      reportType: reportData.reportType,
      results: reportData.results || {}, // Include full results with tasComponents
      // Denormalized fields for quick access in admin panel
      currentTAS: reportData.results?.currentTAS,
      projectedTAS: reportData.results?.projectedTAS
    };

    // Add ROI-specific fields if this is an ROI analysis report
    if (reportData.hasROIAnalysis && reportData.enhancedData) {
      const enhancedSavings = reportData.enhancedData.enhancedSavings;
      if (enhancedSavings) {
        sessionUpdate.year1Savings = enhancedSavings.year1Savings?.totalAnnualSavings;
        sessionUpdate.steadyStateSavings = enhancedSavings.steadyStateSavings?.totalAnnualSavings;
        sessionUpdate.totalAnnualSavings = enhancedSavings.steadyStateSavings?.totalAnnualSavings ||
                                          enhancedSavings.totalAnnualSavings;
      }
      sessionUpdate.year1ROI = reportData.enhancedData.year1ROI;
      sessionUpdate.steadyStateROI = reportData.enhancedData.steadyStateROI;
      sessionUpdate.paybackMonth = reportData.enhancedData.paybackPeriod;
    }

    batch.set(sessionRef, sessionUpdate, { merge: true });

    // Commit all operations atomically
    await batch.commit();

    return reportId;
  } catch (error) {
    console.error('Error saving finalized report:', error);
    throw error;
  }
}

/**
 * Retrieve a finalized report by ID
 * @param reportId - The report ID to retrieve
 * @returns The stored report data or null if not found
 */
export async function getReportById(reportId: string): Promise<StoredReportData | null> {
  try {
    // First, try to get the report from the reports collection (works from any device)
    const publicReportRef = doc(db, 'reports', reportId);
    const publicReportSnap = await getDoc(publicReportRef);
    
    if (publicReportSnap.exists()) {
      const data = publicReportSnap.data();
      // Remove only userId from the public data, but keep sessionId for tracking
      const { userId, ...reportData } = data;
      return reportData as StoredReportData;
    }
    
    // Fallback: Try to get userId and sessionId from localStorage (for backward compatibility)
    const reportMetadata = localStorage.getItem(`report_${reportId}`);
    if (!reportMetadata) {
      console.error('Report not found in reports collection and no localStorage metadata');
      return null;
    }
    
    const { userId, sessionId } = JSON.parse(reportMetadata);
    
    const reportRef = doc(db, 'users', userId, 'calculator_sessions', sessionId, 'finalized_reports', reportId);
    const reportSnap = await getDoc(reportRef);
    
    if (reportSnap.exists()) {
      return reportSnap.data() as StoredReportData;
    }
    
    return null;
  } catch (error) {
    console.error('Error retrieving report:', error);
    return null;
  }
}

/**
 * Store report metadata in localStorage for retrieval
 * This is a temporary solution - in production, consider using a reports collection
 * or collection group queries
 */
export function storeReportMetadata(reportId: string, userId: string, sessionId: string): void {
  localStorage.setItem(`report_${reportId}`, JSON.stringify({ userId, sessionId }));
}

/**
 * Check if a report exists
 * @param reportId - The report ID to check
 * @returns True if the report exists
 */
export async function reportExists(reportId: string): Promise<boolean> {
  const report = await getReportById(reportId);
  return report !== null;
}